import useDocumentMeta from "../hooks/useDocumentMeta.js";
import usePageLanguage from "../hooks/usePageLanguage.js";
import { alternatesFor, canonicalUrlFor } from "../content/pages/registry.js";
import usePageShell from "../hooks/usePageShell.js";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import CookieBanner from "../components/common/CookieBanner";
import useAnalyticsContext from "../hooks/useAnalyticsContext.js";
import { trackSectionViewed, useSectionOnceObserver } from "../lib/analytics";
import PhotoSharingEditorial from "../components/wedding-photo-sharing/PhotoSharingEditorial";
import PlanningHero from "../components/wedding-planning/PlanningHero";
import PlanningDemo from "../components/wedding-planning/PlanningDemo";
import PlanningPlan from "../components/wedding-planning/PlanningPlan";
import PlanningFaq from "../components/wedding-planning/PlanningFaq";
import PlanningFinalCta from "../components/wedding-planning/PlanningFinalCta";
import { WPL_SECTION } from "../components/wedding-planning/sections.js";

/**
 * /wedding-planning
 *
 * The third SEO/GEO page. /digital-wedding-invitation is the "Before" of the
 * couple's arc and /wedding-photo-sharing the "During and After"; this one is
 * the work that runs underneath all of it. Same visual language on purpose —
 * it should read as another chapter, not a different site.
 *
 * ── Crawlability ──────────────────────────────────────────────────────────
 * This route also ships as a real static document (`wedding-planning.html` at
 * the project root, built as a fourth Vite entry). That file — not this
 * component — is what a crawler that does not run JavaScript receives, and it
 * carries the title, description, canonical, robots directive, social tags,
 * JSON-LD and every heading, paragraph and FAQ answer below. React replaces it
 * on mount. `weddingPlanning.seo.test.mjs` asserts the two never drift apart,
 * and pins the claims the product audit ruled out.
 *
 * The meta hook still runs, because a visitor who arrives by client-side
 * navigation gets no static head.
 *
 * ── The claim boundaries that govern the whole page ───────────────────────
 * Four, and every one of them is checked in the test rather than trusted:
 *
 *  1. **Heirloom-only.** The organizer router is mounted behind
 *     `requireProPlanWithCode` in its entirety (api/routes/organizer.routes.mjs),
 *     as is the assistant. There is no smaller Planning Hub on a lesser plan —
 *     there is none at all. `PlanningPlan` is what keeps the rest of the page
 *     honest, which is why it sits before the FAQ rather than after it.
 *  2. **No vendor discovery.** A vendor exists only because the couple typed
 *     its name. Nothing in the product searches for, contacts, books or pays
 *     anyone, so no sentence here may suggest a marketplace or a directory.
 *  3. **A budget, not a ledger.** `OrganizerPayment` is a row with an amount, a
 *     due date and a status of `due` or `paid`. No document is issued to anyone
 *     and no money moves through Agapaé.
 *  4. **Éros suggests; the couple decides.** Éros can write, but only when
 *     asked, and it is blocked entirely inside the sandbox
 *     (`blockSandboxAssistant`). Nothing here may read as autonomous planning —
 *     the Terms already say it is provided as is and may be wrong, and the
 *     marketing page must not promise more than the contract does.
 *
 * ── Page shell ────────────────────────────────────────────────────────────
 * `usePageShell` — the shared bootstrap every page runs. See the hook for why
 * it re-initialises the Webflow engine from a layout effect rather than after
 * paint.
 *
 * `wps-page dwi-page` on the wrapper, not a third scope class: the invitation
 * page's `dwi-demo` block is exactly the framed offer this page needs for the
 * sandbox, and duplicating it under a third prefix would guarantee the two
 * drift. Only the one layout neither sibling has (`wpl-cross-links`) is new.
 */
/**
 * This page's language-neutral path. The canonical and the alternates are
 * derived from it through the page registry rather than written out: the
 * canonical is self-referencing (a Greek page canonicalised to English is a
 * Greek page asking not to be indexed), and the alternates are the reciprocal
 * set the static documents declare, from the same one source.
 */
const PATH = "/wedding-planning";

const WeddingPlanning = () => {
  const analyticsContext = useAnalyticsContext();

  const language = usePageLanguage();

  useDocumentMeta({
    titleKey: "meta.weddingPlanning.title",
    descriptionKey: "meta.weddingPlanning.description",
    canonical: canonicalUrlFor(PATH, language),
    alternates: alternatesFor(PATH),
    robots: "index, follow",
  });

  // Money is the question this page is really answering, so the budget section
  // gets its own impression. `PhotoSharingEditorial` stays presentational — the
  // observer rides on a wrapper here rather than being pushed into a component
  // three pages share.
  const budgetRef = useSectionOnceObserver(() =>
    trackSectionViewed(WPL_SECTION.BUDGET, analyticsContext),
  );
  const erosRef = useSectionOnceObserver(() =>
    trackSectionViewed(WPL_SECTION.EROS, analyticsContext),
  );

  usePageShell();

  return (
    <div className="page-wrapper wps-page dwi-page">
      <Navbar />
      <CookieBanner />
      <main className="main">
        <PlanningHero />

        <PhotoSharingEditorial
          headingKey="weddingPlanning.overview.heading"
          bodyKeys={[
            "weddingPlanning.overview.body1",
            "weddingPlanning.overview.body2",
            "weddingPlanning.overview.body3",
          ]}
          noteKey="weddingPlanning.overview.note"
          image={{
            src: "/assets/images/lovely_couple_003.jpeg",
            altKey: "weddingPlanning.overview.imageAlt",
            width: 1280,
            height: 714,
          }}
        />

        <PhotoSharingEditorial
          headingKey="weddingPlanning.vendors.heading"
          bodyKeys={[
            "weddingPlanning.vendors.body1",
            "weddingPlanning.vendors.body2",
            "weddingPlanning.vendors.body3",
          ]}
          noteKey="weddingPlanning.vendors.note"
          image={{
            src: "/assets/images/invitation/invitation-2.jpg",
            altKey: "weddingPlanning.vendors.imageAlt",
            width: 6558,
            height: 3659,
          }}
          imageSide="left"
        />

        <PhotoSharingEditorial
          headingKey="weddingPlanning.tasks.heading"
          bodyKeys={[
            "weddingPlanning.tasks.body1",
            "weddingPlanning.tasks.body2",
            "weddingPlanning.tasks.body3",
          ]}
          noteKey="weddingPlanning.tasks.note"
          image={{
            src: "/assets/images/bride_shoes.jpg",
            altKey: "weddingPlanning.tasks.imageAlt",
            width: 3000,
            height: 2000,
          }}
        />

        <div ref={budgetRef}>
          <PhotoSharingEditorial
            headingKey="weddingPlanning.budget.heading"
            bodyKeys={[
              "weddingPlanning.budget.body1",
              "weddingPlanning.budget.body2",
              "weddingPlanning.budget.body3",
            ]}
            noteKey="weddingPlanning.budget.note"
          />
        </div>

        <PhotoSharingEditorial
          headingKey="weddingPlanning.categories.heading"
          bodyKeys={[
            "weddingPlanning.categories.body1",
            "weddingPlanning.categories.body2",
            "weddingPlanning.categories.body3",
          ]}
          noteKey="weddingPlanning.categories.note"
          image={{
            src: "/assets/photos/venice_luxury_wedding_photographer_1_von_1.jpg",
            altKey: "weddingPlanning.categories.imageAlt",
            width: 2400,
            height: 1602,
          }}
          imageSide="left"
        />

        <PhotoSharingEditorial
          headingKey="weddingPlanning.documents.heading"
          bodyKeys={[
            "weddingPlanning.documents.body1",
            "weddingPlanning.documents.body2",
          ]}
        />

        <PhotoSharingEditorial
          headingKey="weddingPlanning.timeline.heading"
          bodyKeys={[
            "weddingPlanning.timeline.body1",
            "weddingPlanning.timeline.body2",
            "weddingPlanning.timeline.body3",
          ]}
          noteKey="weddingPlanning.timeline.note"
          image={{
            src: "/assets/images/moments/moment_1.jpg",
            altKey: "weddingPlanning.timeline.imageAlt",
            width: 6558,
            height: 3659,
          }}
        />

        <div ref={erosRef}>
          <PhotoSharingEditorial
            headingKey="weddingPlanning.eros.heading"
            bodyKeys={[
              "weddingPlanning.eros.body1",
              "weddingPlanning.eros.body2",
              "weddingPlanning.eros.body3",
            ]}
            noteKey="weddingPlanning.eros.note"
          />
        </div>

        <PlanningDemo />

        <PlanningPlan />

        <PlanningFaq />

        <PlanningFinalCta />
      </main>
      <Footer />
    </div>
  );
};

export default WeddingPlanning;
